import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import * as authService from "../services/authService";
import { isValidEmail } from "../utils/validators";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("idle");
  const [resendEmail, setResendEmail] = useState("");
  const [resendError, setResendError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus("missing-token");
        return;
      }

      try {
        setStatus("loading");

        const data = await authService.verifyEmail(token);

        if (data?.statusCode && data.statusCode !== 200) {
          throw new Error(data.message || "Email verification failed.");
        }

        navigate("/action-status", {
          replace: true,
          state: {
            status: "success",
            eyebrow: "Email Verified",
            title: "Your email has been verified successfully",
            message:
              data?.message || "Your account is now active and ready to use.",
            note:
              "You can now sign in and continue booking with confidence.",
            actions: [
              {
                label: "Go to Sign In",
                to: "/login",
                variant: "primary",
              },
              {
                label: "Back to Home",
                to: "/",
                variant: "secondary",
              },
            ],
          },
        });
      } catch (err) {
        setStatus("failed");
      }
    };

    verify();
  }, [navigate, token]);

  const handleResend = async (event) => {
    event.preventDefault();
    setResendError("");

    if (!isValidEmail(resendEmail)) {
      setResendError("Please enter a valid email address.");
      return;
    }

    try {
      setResendLoading(true);

      const trimmedEmail = resendEmail.trim();
      const data = await authService.resendVerification(trimmedEmail);

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Could not resend verification email.");
      }

      navigate("/action-status", {
        replace: true,
        state: {
          status: "email",
          eyebrow: "Verification Email Sent",
          title: "Check your email for a new verification link",
          message:
            data?.message || "A new verification email has been sent successfully.",
          note:
            "Please open your inbox and click the new verification link to activate your account. If you do not see it right away, check your spam or junk folder too.",
          details: [
            {
              label: "Email Address",
              value: trimmedEmail,
            },
            {
              label: "Next Step",
              value: "Open the email and click the verification link.",
            },
          ],
          actions: [
            {
              label: "Go to Sign In",
              to: "/login",
              variant: "primary",
            },
            {
              label: "Back to Register",
              to: "/register",
              variant: "secondary",
            },
          ],
        },
      });
    } catch (err) {
      setResendError(err?.message || "We could not resend the verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="page-section">
        <PageContainer width="md">
          <div className="mx-auto max-w-2xl content-card-lg p-8">
            <SectionTitle
              eyebrow="Email Verification"
              title="Verifying your email"
              subtitle="Please wait while we confirm your email address."
              className="mb-8"
            />

            <div className="rounded-2xl border border-[#d7c2a3] bg-[#faf6ef] p-5">
              <p className="text-sm font-medium text-[#3b2f2f]">
                Verifying your email...
              </p>
            </div>
          </div>
        </PageContainer>
      </div>
    );
  }

  if (status === "missing-token" || status === "failed") {
    return (
      <div className="page-section">
        <PageContainer width="md">
          <div className="mx-auto max-w-2xl content-card-lg p-8">
            <SectionTitle
              eyebrow="Email Verification"
              title="We could not verify your email"
              subtitle={
                status === "missing-token"
                  ? "Your verification link is incomplete or missing important information."
                  : "Your verification link may be invalid, expired, or already used."
              }
              className="mb-8"
            />

            <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
              <p className="text-sm text-red-700">
                You can request a new verification email below.
              </p>
            </div>

            <div className="mt-8 rounded-2xl border border-[#d7c2a3] bg-white p-6">
              <h2 className="text-lg font-semibold text-henna">
                Resend Verification Email
              </h2>

              <form onSubmit={handleResend} className="mt-5 space-y-4">
                <Input
                  label="Email"
                  name="resendEmail"
                  type="email"
                  value={resendEmail}
                  onChange={(event) => {
                    setResendEmail(event.target.value);
                    setResendError("");
                  }}
                  error={resendError}
                  placeholder="Enter your email address"
                />

                <Button type="submit" variant="primary" loading={resendLoading}>
                  Resend Verification Email
                </Button>
              </form>
            </div>
          </div>
        </PageContainer>
      </div>
    );
  }

  return null;
};

export default VerifyEmail;